import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { ASSETS } from '../config/assets';
import { Zap, Settings, GraduationCap, Rocket, Smartphone, CheckCircle, Cpu } from 'lucide-react';

const ImplementationPage = () => {
  const { t, language } = useLanguage();

  const features = [
    {
      icon: Settings,
      title: t('implementation.feature1Title'),
      desc: t('implementation.feature1Desc'),
    },
    {
      icon: GraduationCap,
      title: t('implementation.feature2Title'),
      desc: t('implementation.feature2Desc'),
    },
    {
      icon: Rocket,
      title: t('implementation.feature3Title'),
      desc: t('implementation.feature3Desc'),
    },
    {
      icon: Smartphone,
      title: t('implementation.feature4Title'),
      desc: t('implementation.feature4Desc'),
    },
  ];

  const zeroIntegration = language === 'fr' ? [
    'Zéro intégration',
    'Zéro dépendance POS',
    'Fonctionne de manière indépendante',
    'Léger et facile à déployer'
  ] : [
    'Zero integration necessary',
    'Zero POS dependency',
    'Runs independently',
    'Lightweight and easy to deploy'
  ];

  return (
    <div data-testid="implementation-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-darkblue/30 to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <Zap className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                {t('implementation.title')}
              </span>
            </div>
            <h1 
              data-testid="implementation-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
            >
              {t('implementation.subtitle')}
            </h1>
            <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
              {t('implementation.heroDesc')}
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                data-testid="implementation-cta"
                className="btn-demo-cta text-white rounded-lg px-8 py-6"
              >
                {t('nav.requestDemo')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portal Screenshot */}
      <section className="py-12 lg:py-16 bg-[#0D0D0D]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <img
              src={ASSETS.PORTAL_LOGIN}
              alt="YUL On Tap Portal"
              className="rounded-2xl shadow-2xl w-full object-cover border border-white/10"
            />
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0D0D0D]/40 to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Zero Integration Section - Prominent */}
      <section className="py-16 bg-[#1A1A1A] border-t border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-green-500/10 rounded-full border border-green-500/30 mb-4">
              <Cpu className="w-5 h-5 text-green-400" strokeWidth={1.5} />
              <span className="text-sm font-bold uppercase tracking-wider text-green-400">
                {language === 'fr' ? 'Déploiement Indépendant' : 'Independent Deployment'}
              </span>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-white mb-4">
              {language === 'fr' 
                ? 'Pas d\'Intégrations Compliquées. Pas de Complexité IT.'
                : 'No Messy Integrations. No IT Complexity.'
              }
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              {language === 'fr'
                ? "YUL On Tap vit entièrement par lui-même. Notre système fonctionne aux côtés de votre entreprise — simple, rapide et indépendant."
                : "YUL On Tap lives entirely on its own. Our system runs alongside your business — simple, fast, and independent."
              }
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {zeroIntegration.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/80 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`implementation-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="p-3 bg-blue-500/10 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImplementationPage;
